// src/pages/Login.js

import React, { useEffect, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@mui/material'
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined'

// Çoklu dil desteği
import { useTranslation } from 'react-i18next'

export default function Login() {
  const { t } = useTranslation()  // i18n Hook

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const { login, logout, currentUser, userStatus } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      setError('')
      await login(email, password)
    } catch (err) {
      setError(t('loginPage.errorLogin'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (currentUser && !currentUser.isAnonymous && userStatus === 'passive') {
      setError(t('loginPage.accountPassive'))
      logout()
      return
    }

    if (currentUser && !currentUser.isAnonymous && userStatus === 'active') {
      // currentUser gelince login tamam demektir
      navigate('/ProjectsPage')
    }
  }, [currentUser, navigate, logout, t, userStatus])

  useEffect(() => {
    if (location.state?.accountPassive) {
      setError(t('loginPage.accountPassive'))
    }
  }, [location.state, t])

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      minHeight: 54,
      borderRadius: 2.5,
      bgcolor: '#f8fafb',
      transition: 'background-color 160ms, box-shadow 160ms',
      '& fieldset': { borderColor: '#dce7ee' },
      '&:hover fieldset': { borderColor: '#8ebbd5' },
      '&.Mui-focused': {
        bgcolor: '#fff',
        boxShadow: '0 0 0 4px rgba(52,127,177,.1)',
      },
      '&.Mui-focused fieldset': { borderColor: '#347fb1' },
    },
    '& .MuiInputLabel-root.Mui-focused': { color: '#347fb1' },
  }

  return (
    <Box
      component="main"
      sx={{
        minHeight: { xs: 'calc(100vh - 64px)', md: 'calc(100vh - 72px)' },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: { xs: 2, sm: 3 },
        py: { xs: 4, md: 6 },
        bgcolor: '#f4f8fa',
        backgroundImage: 'radial-gradient(circle at 12% 15%, rgba(52,127,177,.12), transparent 28%), radial-gradient(circle at 88% 85%, rgba(217,154,24,.1), transparent 24%)',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 1040,
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '0.92fr 1.08fr' },
          overflow: 'hidden',
          border: '1px solid #dce7ee',
          borderRadius: { xs: 3, md: 4 },
          boxShadow: '0 26px 70px rgba(24,53,74,.13)',
        }}
      >
        <Box
          sx={{
            position: 'relative',
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            justifyContent: 'space-between',
            minHeight: 590,
            p: 6,
            overflow: 'hidden',
            color: '#fff',
            bgcolor: '#18354a',
            backgroundImage: 'linear-gradient(145deg, #18354a 10%, #245a78 62%, #347fb1 100%)',
            '&::before': {
              content: '""',
              position: 'absolute',
              width: 280,
              height: 280,
              right: -120,
              top: -105,
              border: '58px solid rgba(255,255,255,.06)',
              borderRadius: '50%',
            },
            '&::after': {
              content: '""',
              position: 'absolute',
              width: 190,
              height: 190,
              left: -85,
              bottom: -90,
              bgcolor: 'rgba(217,154,24,.18)',
              borderRadius: '50%',
            },
          }}
        >

          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography component="p" sx={{ mb: 1.5, color: '#d99a18', fontSize: 12, fontWeight: 900, letterSpacing: 1.4 }}>
              POPULI DOCUMENT INTELLIGENCE
            </Typography>
            <Typography component="h2" sx={{ maxWidth: 370, fontSize: 34, fontWeight: 850, lineHeight: 1.18, letterSpacing: -0.7 }}>
              Kurumsal bilginizi tek yerde yönetin.
            </Typography>
            <Typography sx={{ mt: 2, maxWidth: 370, color: 'rgba(255,255,255,.76)', fontSize: 15, lineHeight: 1.7 }}>
              Dokümanlarınıza güvenle erişin, raporlarınızı takip edin ve Populibot ile ihtiyaç duyduğunuz bilgiye hızla ulaşın.
            </Typography>

            <Box sx={{ display: 'grid', gap: 1.5, mt: 4 }}>
              {['Güvenli doküman yönetimi', 'Departmana özel erişim', 'Akıllı arama ve raporlama'].map((feature) => (
                <Box key={feature} sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                  <CheckCircleRoundedIcon sx={{ color: '#d99a18', fontSize: 20 }} />
                  <Typography sx={{ color: 'rgba(255,255,255,.9)', fontSize: 14, fontWeight: 650 }}>{feature}</Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <Typography sx={{ position: 'relative', zIndex: 1, color: 'rgba(255,255,255,.5)', fontSize: 11.5 }}>
            Güvenli ve merkezi çalışma alanınız
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', px: { xs: 3, sm: 6, md: 7 }, py: { xs: 5, sm: 7 } }}>
          <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'center', mb: 4 }}>
            <Box component="img" src="/logo.png" alt="Populi" sx={{ width: 82, height: 'auto' }} />
          </Box>

          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, alignSelf: 'flex-start', px: 1.25, py: 0.7, mb: 2.5, borderRadius: 2, color: '#347fb1', bgcolor: '#eaf4fa' }}>
            <LockOutlinedIcon sx={{ fontSize: 17 }} />
            <Typography sx={{ fontSize: 11.5, fontWeight: 850, letterSpacing: 0.35 }}>GÜVENLİ GİRİŞ</Typography>
          </Box>

          <Typography component="h1" sx={{ color: '#18354a', fontSize: { xs: 28, sm: 32 }, fontWeight: 900, lineHeight: 1.2, letterSpacing: -0.5 }}>
            {t('loginPage.title')}
          </Typography>
          <Typography sx={{ mt: 1, color: '#6b7f8d', fontSize: 14.5, lineHeight: 1.6 }}>
            Çalışma alanınıza devam etmek için bilgilerinizi girin.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mt: 2.5, borderRadius: 2, alignItems: 'center' }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 3.5 }}>
            <TextField
              required
              fullWidth
              type="email"
              label={t('loginPage.emailLabel')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              autoFocus
              sx={fieldSx}
            />

            <TextField
              required
              fullWidth
              type={showPassword ? 'text' : 'password'}
              label={t('loginPage.passwordLabel')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              sx={{ ...fieldSx, mt: 2 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((visible) => !visible)}
                      onMouseDown={(event) => event.preventDefault()}
                      edge="end"
                      aria-label={showPassword ? 'Şifreyi gizle' : 'Şifreyi göster'}
                      sx={{ color: '#6b7f8d' }}
                    >
                      {showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1.5 }}>
              <Link to="/forgot-password" style={{ color: '#347fb1', fontSize: 13.5, fontWeight: 750, textDecoration: 'none' }}>
                {t('loginPage.forgotPassword')}
              </Link>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              endIcon={!loading ? <ArrowForwardRoundedIcon /> : null}
              sx={{
                minHeight: 52,
                mt: 3.5,
                borderRadius: 2.5,
                bgcolor: '#18354a',
                fontSize: 15,
                fontWeight: 850,
                textTransform: 'none',
                boxShadow: '0 10px 24px rgba(24,53,74,.2)',
                '&:hover': { bgcolor: '#347fb1', boxShadow: '0 12px 28px rgba(52,127,177,.25)' },
              }}
            >
              {loading ? <CircularProgress size={23} color="inherit" /> : t('loginPage.loginButton')}
            </Button>
          </Box>

          <Typography sx={{ mt: 3, color: '#8a9aa5', fontSize: 11.5, lineHeight: 1.6, textAlign: 'center' }}>
            Hesabınızla ilgili desteğe ihtiyaç duyarsanız yöneticinizle iletişime geçin.
          </Typography>
        </Box>
      </Paper>
    </Box>
  )
}
